<template>
  <v-card class="auth-form">
    <v-progress-linear v-show="isSubmitting || pendingUserListRetrieval" indeterminate></v-progress-linear>
    <div :class="pendingUserListRetrieval ? 'disabled-overlay' : ''"></div>
    <div>
      <v-layout column>
        <v-flex class="auth-form__header">
          <icon-ypo-with-text class="auth-form__header--icon"></icon-ypo-with-text>
        </v-flex>
        <v-flex>
          <v-layout class="auth-form__content" column>
            <v-flex v-if="cardTitle" class="auth-form__content--title">{{ cardTitle }}</v-flex>
            <component
                :is="determineLoginForm()"
                :changeCardState="changeState"
                :isSubmitting="isSubmitting"
                :returnToLogin="returnToLogin"
                :toggleProgressIndicator="toggleProgressIndicator"
                keep-alive
            >
            </component>
          </v-layout>
        </v-flex>
      </v-layout>
    </div>
    <v-layout class="auth-form__footer" column>
      <v-flex xs12>
        <v-layout align-center justify-center row>
          <v-flex v-if="!isExternalSpeaker">
            <v-btn
                class="auth-form__footer--button"
                data-qe-id="privacy_link"
                flat
                small
                @click="forwardToExternalLink(loginConstants.externalLinks.privacy.DB_NAME)"
            >
              {{ loginConstants.externalLinks.privacy.DISPLAY_NAME }}
            </v-btn>
          </v-flex>

          <v-flex>
            <v-btn
                class="auth-form__footer--button"
                data-qe-id="terms-of-use_link"
                flat
                small
                @click="forwardToExternalLink(termsOfUse)"
            >
              {{ termsOfUse }}
            </v-btn>
          </v-flex>
          <v-flex>
            <v-menu
                attach
                nudge-left="116"
                nudge-top="33"
                origin="right right"
                top=""
                transition="scale-transition"
            >
              <v-btn slot="activator" class="auth-form__footer--button" flat small>
                {{ loginConstants.contactUs.CONTACT_US_LINK }}
              </v-btn>
              <v-layout class="contact-us" column>
                <v-flex
                    v-for="(info, index) in (isExternalSpeaker ? loginConstants.contactUs.speakerContactTypes : loginConstants.contactUs.contactTypes)"
                    :key="index"
                    class="contact-us__item"
                    xs12
                >
                  <div class="contact-us__item-title">{{ info.title }}</div>
                  <div class="contact-us__item-detail">
                      <span
                          v-if="!isExternalSpeaker || info.title !== 'Email'"
                          :class="
                              info.type === loginConstants.contactUs.types.EMAIL ? 'pointer' : ''
                          "
                          data-qe-id="contact-us_link"
                          id="login-card-contactus-email"
                          @click="launchEmailClient(info)"
                      >
                        {{ info.detail }}
                      </span>
                    <span
                        v-if="info.title === 'Email' && isExternalSpeaker"
                        :class="info.type === loginConstants.contactUs.types.EMAIL ? 'pointer' : ''"
                        data-qe-id="speaker-contact-us_link"

                        @click="launchSpeakerEmailClient(loginConstants.ISSpeakerEmail)"
                    >
                      {{ loginConstants.ISSpeakerEmail }}
                    </span>
                  </div>
                </v-flex>
              </v-layout>
            </v-menu>
          </v-flex>
        </v-layout>
      </v-flex>
      <v-flex class="auth-form__copyright" xs-12>
        &copy; {{ loginConstants.YPO }}{{ new Date().getFullYear() }}
      </v-flex>
    </v-layout>
  </v-card>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { loginConstants } from './login.constants';
import LoginDefaultForm from './LoginDefaultForm';
import ForgotUsername from './ForgotUsername/ForgotUsername';
import ForgotPassword from './ForgotPassword/ForgotPassword';
import RecoveryOptions from './RecoveryOptions/RecoveryOptions';
import RecoveryCodeForm from './RecoveryCodeForm/RecoveryCodeForm';
import UsernameFurtherAssistance from './UsernameFurtherAssistance/UsernameFurtherAssistance';
import PasswordFurtherAssistance from './PasswordFurtherAssistance/PasswordFurtherAssistance';
import UsernameRecoveryOptions from './UsernameRecoveryOptions/UsernameRecoveryOptions';
import SelectTypePasswordDelivery from './PasswordFurtherAssistance/SelectTypePasswordDelivery';
import AccountLockout from './AccountLockout/AccountLockout';
import FurtherSecurity from './FurtherSecurity/FurtherSecurity';
import ContactUsForFurtherAssistance from './FurtherSecurity/ContactUsForFurtherAssistance';
import PasswordCodeSent from './PasswordCodeSent/PasswordCodeSent';
import ResetPassword from './ResetPassword/ResetPassword';
import EnterPasswordResetCode from './EnterPasswordResetCode/EnterPasswordResetCode';
import ResetPasswordConfirmationSent from './ResetPasswordConfirmationSent/ResetPasswordConfirmationSent';
import SpousePartnerUsernameRecovery from './SpousePartnerLoginAssistance/SpousePartnerUsernameRecovery';
import SpousePartnerResetPassword from './SpousePartnerLoginAssistance/SpousePartnerResetPassword';
import PrivacyPolicy from './PrivacyPolicy/PrivacyPolicy.vue';
import { storeAction } from '../../store/storeActions.constant';
import { storeGetters } from '../../store/storeGetters.constant';
import { storeMutation } from '../../store/storeMutations.constant';
import IconYpoWithText from "../Icons/IconYpoWithText";
import {gaDataLayerEvent} from "@/plugins/analytics";
import {gtmEventName} from "@/plugins/gtmAnalytics";

export default {
  name: 'LoginCard',
  components: {
    IconYpoWithText,
    LoginDefaultForm,
    ForgotUsername,
    ForgotPassword,
    RecoveryOptions,
    RecoveryCodeForm,
    UsernameFurtherAssistance,
    PasswordFurtherAssistance,
    UsernameRecoveryOptions,
    SelectTypePasswordDelivery,
    PasswordCodeSent,
    ResetPassword,
    EnterPasswordResetCode,
    ResetPasswordConfirmationSent,
    AccountLockout,
    FurtherSecurity,
    ContactUsForFurtherAssistance,
    SpousePartnerUsernameRecovery,
    SpousePartnerResetPassword,
    PrivacyPolicy,
  },
  props: {
    defaultComponent: {
      type: String,
    },
  },
  data: () => ({
    loginConstants,
    isSubmitting: false,
    currentComponent: 'ForgotUsername',
    componentNames: loginConstants.componentNames,
    termsOfUse: loginConstants.externalLinks.termsOfUse.DISPLAY_NAME,
  }),
  created() {
    if (this.defaultComponent) {
      this.currentComponent = this.defaultComponent;
    } else {
      const { resetFlow } = this.$route.query;
      if (resetFlow && resetFlow === loginConstants.PASSWORD.toLowerCase()) {
        this.currentComponent = loginConstants.componentNames.forgotPassword.component;
      } else if (resetFlow && resetFlow === loginConstants.USERNAME.toLowerCase()) {
        this.currentComponent = loginConstants.componentNames.forgotUsername.component;
      }
    }
    this.externalSpeaker();
  },
  methods: {
    ...mapActions({
      clearErrorOnStateChange: `auth/${storeAction.handleLoginErrorMessage}`,
      initiateLoginRedirect: `auth/${storeAction.initiateLoginRedirect}`,
    }),
    ...mapMutations({
      setExternalSpeaker: `auth/${storeMutation.setExternalSpeaker}`,
    }),

    externalSpeaker() {
      this.setExternalSpeaker(this.$route.query.isSpeaker === 'true' || this.$route.query.isspeaker === 'true');
    },
    determineLoginForm() {
      return this.currentComponent;
    },
    toggleProgressIndicator() {
      this.isSubmitting = !this.isSubmitting;
    },
    changeState(component) {
      this.clearErrorOnStateChange(null);
      this.currentComponent = component;
    },
    returnToLogin() {
      if (window.location.hash.includes('#/forgotusername?resetFlow=username')) {
        gaDataLayerEvent(gtmEventName.C_Login_RecoverUN_ReturnToLogin, '')
      }
      //return back to Okta for login
      this.initiateLoginRedirect();
    },
    forwardToExternalLink(displayName) {
      const privacyObject = loginConstants.externalLinks.privacy;
      const termsObject = loginConstants.externalLinks.termsOfUse;
      if (this.termsPrivacyURLs && displayName !== "") {
        const linkObjects = this.termsPrivacyURLs.find(item => item.name.toLowerCase() === displayName.toLowerCase());
        window.open(linkObjects.navigationUrl, '_blank');
        if (termsObject.gaTagName) {
            gaDataLayerEvent(termsObject.gaTagName, '');
        }
        if (privacyObject.gaTagName) {
            gaDataLayerEvent(privacyObject.gaTagName, '');
        }      
      }
    },
    launchEmailClient(item) {
      const contactTypes = loginConstants.contactUs.contactTypes;
          const contact = contactTypes.find(contact => contact.detail === item.detail);
          if (contact) {
              if (contact.gaTagName) {
                  gaDataLayerEvent(contact.gaTagName, '');
              }
              window.location = `mailto:${contact.detail}`;
            }
    },
    launchSpeakerEmailClient(speakerEmail) {
      window.location = `mailto:${speakerEmail}`;
    },
  },
  computed: {
    ...mapState('actionState', ['actions']),
    ...mapGetters({
      isExternalSpeaker: `auth/${storeGetters.isExternalSpeaker}`,
      termsPrivacyURLs: `openAPI/${storeGetters.termsPrivacyURLs}`,
    }),
    cardTitle() {
      if (this.currentComponent) {
        const currentCard = Object.keys(this.componentNames).find(key => this.componentNames[key].component.toLowerCase() === this.currentComponent.toLowerCase());
        return this.componentNames[currentCard].title;
      }
    },
    pendingUserListRetrieval() {
      return storeAction && (
          (this.actions[storeAction.getPermissions] && this.actions[storeAction.getPermissions].pending) ||
          (this.actions[storeAction.getProfileInformation] && this.actions[storeAction.getProfileInformation].pending)
      );
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../common/styles/_colors.scss';
@import '../../common/styles/_ypo-auth.scss';

.v-progress-linear {
  position: absolute;
  margin: 0;
  top: 0;
}
</style>
